import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { PartyRole } from '@hero/schema';

/**
 * Tabs supported by the client workspace right pane.
 *
 * `info` is the canonical landing tab; `pricing`/`agreements`/`invoices` are
 * gated/coming-soon and may render their own internal empty state.
 */
export type ClientWorkspaceTab =
  | 'info'
  | 'contacts'
  | 'places'
  | 'orders'
  | 'pricing'
  | 'agreements'
  | 'invoices';

export const CLIENT_WORKSPACE_TABS: readonly ClientWorkspaceTab[] = [
  'info',
  'contacts',
  'places',
  'orders',
  'pricing',
  'agreements',
  'invoices',
] as const;

/**
 * Tabs rendered in the workspace tab strip. Subset of
 * {@link CLIENT_WORKSPACE_TABS} — hidden tabs are still valid URL
 * destinations (their `?tab=…` value still hydrates the store and
 * the page still renders them) so direct deep-links keep working.
 *
 * - `agreements` is hidden post-Phase-4: the contract envelope is
 *   surfaced inline on the Info tab via `AgreementInfoSection` (one
 *   open agreement per `(org, client)` pair by backend rule, so a
 *   dedicated tab for a zero-or-one entity is overkill — and the
 *   Pricing tab kept feeling crowded with a contract strip glued
 *   above the rate table). Power users can still reach the
 *   full-list view via the explicit URL `?tab=agreements`.
 */
export const VISIBLE_CLIENT_WORKSPACE_TABS: readonly ClientWorkspaceTab[] =
  CLIENT_WORKSPACE_TABS.filter(t => t !== 'agreements');

const TABS_SET = new Set<ClientWorkspaceTab>(CLIENT_WORKSPACE_TABS);

export function isClientWorkspaceTab(v: unknown): v is ClientWorkspaceTab {
  return typeof v === 'string' && TABS_SET.has(v as ClientWorkspaceTab);
}

/**
 * Editable sections on the workspace Info tab. Maps 1:1 to a section modal
 * (legal/settings/brand) or to inline editors composed on the page.
 */
export type ClientWorkspaceSection =
  | 'identity'
  | 'primary_contact'
  | 'brand'
  | 'legal'
  | 'settings'
  | 'ai';

export interface ClientWorkspaceState {
  /** Currently selected client (string id). Mirrored to URL `?clientId=`. */
  selectedClientId?: string;
  /** Active right-pane tab. Mirrored to URL `?tab=` (`info` is omitted). */
  activeTab: ClientWorkspaceTab;
  /** Left-rail search term (live, not URL-synced). */
  search: string;
  /** Optional left-rail role filter. */
  roleFilter?: PartyRole;
  /** Section being edited via a modal. `undefined` = no modal open. */
  editingSection?: ClientWorkspaceSection;
  /** Whether the "+ New client" modal is open. */
  isCreateOpen: boolean;

  selectClient: (id?: string) => void;
  setActiveTab: (tab: ClientWorkspaceTab) => void;
  setSearch: (term: string) => void;
  setRoleFilter: (role?: PartyRole) => void;
  openSectionEdit: (section: ClientWorkspaceSection) => void;
  closeSectionEdit: () => void;
  openCreate: () => void;
  closeCreate: () => void;
  reset: () => void;
}

/**
 * Zustand store backing the carrier-dashboard `/crm` workspace.
 *
 * - Selection (`selectedClientId`) and `activeTab` are intended to be
 *   URL-synced via `useClientWorkspaceUrlSync()`.
 * - `search`, `roleFilter`, `editingSection`, `isCreateOpen` are ephemeral
 *   page-level state; they don't persist to the URL.
 * - Stores ids only. Entity data lives in the React Query cache via
 *   `useClientApi(id)` / `useClientListApi()`.
 */
export const useClientWorkspaceStore = create<ClientWorkspaceState>()(
  immer(set => ({
    selectedClientId: undefined,
    activeTab: 'info',
    search: '',
    roleFilter: undefined,
    editingSection: undefined,
    isCreateOpen: false,

    selectClient: id =>
      set(state => {
        state.selectedClientId = id;
        // Reset to overview tab when changing selection.
        state.activeTab = 'info';
        state.editingSection = undefined;
      }),
    setActiveTab: tab =>
      set(state => {
        state.activeTab = tab;
      }),
    setSearch: term =>
      set(state => {
        state.search = term;
      }),
    setRoleFilter: role =>
      set(state => {
        state.roleFilter = role;
      }),
    openSectionEdit: section =>
      set(state => {
        state.editingSection = section;
      }),
    closeSectionEdit: () =>
      set(state => {
        state.editingSection = undefined;
      }),
    openCreate: () =>
      set(state => {
        state.isCreateOpen = true;
      }),
    closeCreate: () =>
      set(state => {
        state.isCreateOpen = false;
      }),
    reset: () =>
      set(state => {
        state.selectedClientId = undefined;
        state.activeTab = 'info';
        state.search = '';
        state.roleFilter = undefined;
        state.editingSection = undefined;
        state.isCreateOpen = false;
      }),
  })),
);
